/**
 * Client logo / contact photo check by content, not just the file name (mirrors ClientImageValidator on the API):
 * JPG/PNG must start with their signature bytes, an SVG must be an <svg> document with
 * nothing that can run script, and the file must be at most 2 MB.
 */
export const IMAGE_MAX_BYTES = 2000000;
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const UNSAFE_SVG_CONTENT = /<\s*script|\son[a-z]+\s*=|javascript\s*:|<\s*foreignObject|<\s*iframe|<\s*embed|<\s*object|<!ENTITY/i;
function readBytes(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });
}
function startsWith(bytes, signature) {
    return bytes.length >= signature.length && signature.every((value, index) => bytes[index] === value);
}
// Resolves to the reason the file can't be used, or null when it's fine
export function checkImageFile(file) {
    const name = (file.name || "").toLowerCase();
    const extension = name.indexOf(".") > -1 ? name.split(".").pop() : "";
    if (["jpg", "jpeg", "png", "svg"].indexOf(extension) === -1) {
        return Promise.resolve("Please upload an image with the file format as .jpg, .png or .svg.");
    }
    if (file.size > IMAGE_MAX_BYTES) {
        return Promise.resolve("Please upload an image which is less than 2 MB in size.");
    }
    return readBytes(file).then((bytes) => {
        if (extension === "png") {
            return startsWith(bytes, PNG_SIGNATURE) ? null : "The file is not a valid PNG image.";
        }
        if (extension === "svg") {
            const text = new TextDecoder("utf-8").decode(bytes);
            if (!/<svg/i.test(text)) {
                return "The file is not a valid SVG image.";
            }
            return UNSAFE_SVG_CONTENT.test(text) ? "SVG images can't contain scripts, event handlers or embedded content." : null;
        }
        return startsWith(bytes, JPEG_SIGNATURE) ? null : "The file is not a valid JPG image.";
    }, () => "The file could not be read.");
}
